import { FormattedMessage } from 'react-intl';

// Components
import { Grid } from '@fortress-technology-solutions/fortress-component-library/Molecules';
import {
  Switch,
  Divider,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import {
  SetupLabel,
  SetupTitle,
  SetupValue,
  SetupSwitch,
  AnnualRecertMonthPicker,
} from '../shared';
import SetupGridItem from '../../../SetupGridItem';
import ProgramPassbookRate from '../ProgramPassbookRate';

import OnOffTypography from '../../../OnOffTypography';
import CertificationPastDue from '../../components/CertificationPastDue';

// Constants
import messages from './messages';
import messagesAffordableSetup from '../../messages';

// types
import type { PHValues } from '../../../types';

type Props = {
  promptToaster: Function,
  isLoadingPassbookRateData: boolean,
  editMode: boolean,
  onChange: Function,
  values: PHValues,
  intl: Object,
};

const PHSetup = ({
  editMode,
  onChange,
  values,
  promptToaster,
  isLoadingPassbookRateData,
  intl,
}: Props) => {
  const {
    agencyName,
    phaCode,
    PHProgramType,
    PHProjectNumber,
    PHProjectSuffix,
    phProjects,
    PHMinimumRent,
    autoSendToPIC,
    passbookRates,
    autoRolloverIncomeAndAssets,
    PHAnnualRecertMonth,
    hotmaConfig,
    certificationPastDueDefinition,
  } = values;

  const handleAutoSendToPICChange = (event: Object, value: boolean) => {
    onChange({
      target: { name: 'setup.autoSendToPIC', value },
    });
  };

  const handlePHAnnualRecertMonthChange = (date: Object, validate: Object) => {
    onChange({
      target: { name: 'setup.PHAnnualRecertMonth', value: date },
    });
  };

  const validatePHACode = (value: string) => {
    if ((value ?? '').length !== 5) {
      return false;
    }
    return true;
  };

  const normalizedProjects =
    Array.isArray(phProjects) && phProjects.length > 0
      ? phProjects
      : [
          {
            projectNumber: PHProjectNumber ?? null,
            projectSuffix: PHProjectSuffix ?? null,
            minimumRent: PHMinimumRent ?? null,
          },
        ].filter(
          (project) =>
            project.projectNumber ||
            project.projectSuffix ||
            project.minimumRent === 0 ||
            !!project.minimumRent,
        );

  return (
    <Grid container spacing={2}>
      <SetupTitle>
        <FormattedMessage {...messages.title} />
      </SetupTitle>
      <Grid item xs={12} sm={6}>
        <SetupLabel {...messages.agencyName} />
        <SetupValue
          editMode={editMode}
          name="setup.agencyName"
          value={agencyName}
          required={true}
          placeholder=""
          editable={editMode}
          onChange={onChange}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <SetupLabel {...messages.phaCode} />
        <SetupValue
          editMode={editMode}
          name="setup.phaCode"
          minLength={'5'}
          maxLength={'5'}
          value={phaCode}
          placeholder=""
          editable={editMode}
          onChange={onChange}
          required={true}
          validateFunction={(value) => validatePHACode(value)}
          message={messages.phaCodeLengthError}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <SetupLabel {...messages.phProgramType} />
        <SetupValue
          editMode={editMode}
          name="setup.PHProgramType"
          value={PHProgramType}
          placeholder=""
          onChange={onChange}
          editable={editMode}
        />
      </Grid>
      <Grid item xs={12}>
        <Divider />
      </Grid>
      {normalizedProjects.map((project, index) => (
        <Grid
          item
          xs={12}
          key={project.affordableFundingId || `ph-project-${index}`}
        >
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <SetupLabel {...messages.phProjectNumber} />
              <SetupValue
                editMode={editMode}
                name={`setup.phProjects.${index}.projectNumber`}
                value={project.projectNumber}
                required={false}
                editable={false}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <SetupLabel {...messages.phProjectSuffix} />
              <SetupValue
                editMode={editMode}
                name={`setup.phProjects.${index}.projectSuffix`}
                value={project.projectSuffix}
                required={false}
                editable={false}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <SetupLabel {...messages.phMinimumRent} />
              <SetupValue
                editMode={editMode}
                name={`setup.phProjects.${index}.minimumRent`}
                value={
                  project.minimumRent || project.minimumRent === 0
                    ? `$${project.minimumRent}`
                    : project.minimumRent
                }
                required={false}
                editable={false}
              />
            </Grid>
            <Grid item xs={12}>
              <Divider />
            </Grid>
          </Grid>
        </Grid>
      ))}
      <SetupGridItem
        label={<FormattedMessage {...messages.autoSendToPIC} />}
        xs={12}
        sm={12}
      >
        {editMode ? (
          <Switch
            name="setup.autoSendToPIC"
            id="autoSendToPIC-switch"
            checked={autoSendToPIC}
            onChange={handleAutoSendToPICChange}
          />
        ) : (
          <OnOffTypography on={autoSendToPIC}>
            {autoSendToPIC ? (
              <FormattedMessage {...messagesAffordableSetup.on} />
            ) : (
              <FormattedMessage {...messagesAffordableSetup.off} />
            )}
          </OnOffTypography>
        )}
      </SetupGridItem>

      <SetupGridItem
        label={
          <FormattedMessage {...messages.autoRolloverIncomeAssetsAndExpenses} />
        }
        TooltipProps={{
          title: (
            <FormattedMessage
              {...messages.autoRolloverIncomeAssetsAndExpensesDescription}
            />
          ),
        }}
      >
        <SetupSwitch
          switchId={'public-housing-auto-rollover-income-and-assets-switch'}
          editMode={editMode}
          name="setup.PHAutoRolloverIncomeAndAssets"
          disabled={!editMode}
          value={autoRolloverIncomeAndAssets}
          onChange={onChange}
        />
      </SetupGridItem>
      <SetupGridItem
        label={
          <FormattedMessage
            {...messagesAffordableSetup.annualRecertMonth}
            values={{ program: 'Public Housing' }}
          />
        }
      >
        <AnnualRecertMonthPicker
          handleAnnualRecertMonthChange={handlePHAnnualRecertMonthChange}
          editMode={editMode}
          programName={'PH'}
          value={PHAnnualRecertMonth}
        />
      </SetupGridItem>

      <Grid item xs={12} sm={12}>
        <Grid container>
          <Grid item xs={12} sm={6}>
            <SetupLabel {...messagesAffordableSetup.hotmaActive} />
            <OnOffTypography on={hotmaConfig?.hasAdoptedHOTMA ?? false}>
              {hotmaConfig?.hasAdoptedHOTMA ?? false ? (
                <FormattedMessage {...messagesAffordableSetup.on} />
              ) : (
                <FormattedMessage {...messagesAffordableSetup.off} />
              )}
            </OnOffTypography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <SetupLabel {...messagesAffordableSetup.hotmaImplementationDate} />
            <SetupValue
              name="PH.HOTMAEffectiveDate"
              value={
                hotmaConfig?.hasAdoptedHOTMA ?? false
                  ? hotmaConfig?.effectiveDate
                  : '---'
              }
              editable={false}
            />
          </Grid>
          <CertificationPastDue
            editMode={editMode}
            value={certificationPastDueDefinition}
            name="setup.PHCertificationPastDueDefinition"
            onChange={onChange}
          />
        </Grid>
      </Grid>

      <Grid item xs={12}>
        <ProgramPassbookRate
          key={'PH_PassbookRates'}
          programName={'PH'}
          isLoadingPassbookRateData={isLoadingPassbookRateData}
          passbookRates={passbookRates}
        />
      </Grid>
    </Grid>
  );
};

export default PHSetup;
